import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { API } from "../../config/config";
import { api_admin_routes } from "../../config/apiRoutes";
import toast from "react-hot-toast";
import { SliceAdminState } from "../../types/types";

const initialState: SliceAdminState = {
  adminData: null,
  remainingLicenses: 0,
  totalFlats: 0,
  licenses: 0,
  loading: false,
  error: null,
};

export const getAdminData = createAsyncThunk(
  "admin/getAdminData",
  async (userId: any, { rejectWithValue }) => {
    try {
      const response = await API.GET(`${api_admin_routes.admin}/${userId}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateAdminData = createAsyncThunk(
  "admin/updateAdminData",
  async ({ id, newData }: any, { rejectWithValue }) => {
    try {
      const response = await API.PUT(
        `${api_admin_routes.admin}/${id}`,
        newData
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);
export const createAdminUser = createAsyncThunk(
  "admin/createAdminUser",
  async (
    {
      adminId,
      username,
      plainTextPassword,
    }: { adminId: string; username: string; plainTextPassword: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await API.POST(
        `${api_admin_routes.admin}/${adminId}/add-user`,
        { username, plainTextPassword }
      );
      toast.success("Admin user created successfully");
      return response.data;
    } catch (error: any) {
      toast.error("Failed to create admin user.");
      return rejectWithValue(error.response.data);
    }
  }
);

// Delete an admin user
export const deleteAdminUser = createAsyncThunk(
  "admin/deleteAdminUser",
  async (
    { adminId, username }: { adminId: string; username: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await API.DELETE(
        `${api_admin_routes.admin}/${adminId}/delete-user/${username}`
      );
      toast.success("Admin user deleted successfully");
      return response.data;
    } catch (error: any) {
      toast.error("Failed to delete admin user.");
      return rejectWithValue(error.response.data);
    }
  }
);
export const getRemainingLinceces = createAsyncThunk(
  "admin/getRemainingLinceces",
  async ({ adminId }: { adminId?: string }, { rejectWithValue }) => {
    try {
      const response = await API.GET(
        `${api_admin_routes.admin}/remainingLicenses/${adminId}`
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);
export const toggleAdminStatus = createAsyncThunk<
  any,
  { developerId: any; isActive: boolean; callBack?: any }
>(
  "admin/toggleAdminStatus",
  async ({ developerId, isActive, callBack }, { rejectWithValue }) => {
    try {
      const response = await API.PUT(
        `${api_admin_routes.admin}/${developerId}/toggle-status`,
        { isActive }
      );
      if (callBack) callBack(response.data);
      return response.data;
    } catch (error: any) {
      if (callBack) callBack(error.response);
      return rejectWithValue(
        error.message || "Failed to toggle building status"
      );
    }
  }
);

const adminSlice = createSlice({
  name: "admin",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAdminData.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAdminData.fulfilled, (state, action) => {
        state.loading = false;
        state.adminData = action.payload;
      })
      .addCase(getAdminData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as any;
      })
      .addCase(updateAdminData.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateAdminData.fulfilled, (state, action) => {
        state.loading = false;
        state.adminData = action.payload;
      })
      .addCase(updateAdminData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as any;
      })
      .addCase(createAdminUser.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(deleteAdminUser.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(getRemainingLinceces.fulfilled, (state, action) => {
        state.loading = false;
        state.remainingLicenses = action.payload.remainingLicenses;
        state.totalFlats = action.payload.totalFlats;
        state.licenses = action.payload.licenses;
      })
      .addCase(toggleAdminStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
      });
  },
});

export default adminSlice.reducer;
